import { api, type AiMusicInfo, type AiMusicJob, type MusicCategory } from '../sync/api';
import { videoConvert } from '../video/engine';
import { fmtTime } from '../video/project';
import { $, $in } from './dom';
import { toast } from './toast';

/*
 * "ให้ AI แต่งเพลง" (0010, ElevenLabs Music) inside the music library, admins only (choices 1ก 2ก 3ก 4ก):
 *   pick a mood (+ optional words), the length starts as the open video's length, "แต่งเพลง" → the server asks
 *   ElevenLabs for an instrumental song and keeps it as a draft → listen → "เก็บเข้าคลัง" (name + category) or "ทิ้ง".
 *   Usage this month (Thailand time) against the cap is always shown; the server refuses past the cap with the numbers.
 *   No key set → the form is closed and says where to set it.
 */
export interface AiMusicHooks {
  categories: () => MusicCategory[];
  /** reload the library list (after a song is kept) */
  reload: () => Promise<void>;
  /** the open video's length in seconds (0 = none) */
  videoSeconds: () => number;
  online: () => string | null;
  /** admins see everyone's drafts and set the monthly cap; a member with the right sees only their own (0011) */
  isAdmin: () => boolean;
  /** stop the library's own player (one song at a time) */
  stopOthers: () => void;
}

const errText = (e: unknown) => e instanceof Error && e.message ? e.message : String(e);
/** mood → the library category it most likely belongs in */
const MOOD_CATEGORY: Record<string, string> = { fun: 'fun', chill: 'chill', cafe: 'cafe', serious: 'serious', warm: 'chill', hype: 'fun' };
const thaiTime = (iso: string) =>
  new Date(iso).toLocaleString('th-TH', { timeZone: 'Asia/Bangkok', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function initAiMusic(h: AiMusicHooks) {
  const list = $('maList'), audio = $<HTMLAudioElement>('maAudio'), msg = $('maMsg');
  const mood = $<HTMLSelectElement>('maMood'), len = $in('maLen'), go = $<HTMLButtonElement>('maGo');
  let info: AiMusicInfo | null = null;
  let making = false, busy = false;
  let playing: string | null = null;
  let dropId: string | null = null;        // "ทิ้ง" asks once more: the song was paid for
  let tick = 0;

  const say = (t: string, ok = false) => { msg.textContent = t; msg.hidden = !t; msg.classList.toggle('ok', ok); };
  const fitSeconds = () => Math.min(180, Math.max(3, Math.ceil(h.videoSeconds() || 30)));
  const lengthMs = () => Math.round(Number(len.value) * 1000);

  function paintCost() {
    if (!info) return;
    const usedMin = info.usedMs / 60_000, s = Number(len.value);
    const left = Math.max(0, info.capMinutes * 60 - info.usedMs / 1000);
    $('maUsage').textContent = info.capMinutes === 0
      ? `การให้ AI แต่งเพลงปิดอยู่ (เพดาน 0 นาที) ${h.isAdmin() ? 'ตั้งเพดานด้านล่างเพื่อเปิด' : 'ขอให้ผู้ดูแลตั้งเพดานเพื่อเปิด'}`
      : `เดือนนี้ใช้ไป ${usedMin.toFixed(1)} จาก ${info.capMinutes} นาที (≈ $${(usedMin * info.usdPerMinute).toFixed(2)}) · เหลือ ${fmtTime(left)}`;
    $('maCost').textContent = Number.isFinite(s) && s > 0
      ? `เพลงนี้ ${s} วินาที ≈ $${(s / 60 * info.usdPerMinute).toFixed(2)}${s > left ? ' · เกินที่เหลือของเดือนนี้' : ''}`
      : 'ใส่ความยาวเป็นวินาที 3 ถึง 180';
  }

  function paintGo() {
    go.textContent = making ? `กำลังแต่ง… ${tick} วิ (อาจใช้ 1–2 นาที อย่าปิดหน้า อย่าล็อกจอ)` : 'แต่งเพลง (เพลงบรรเลง ไม่มีเสียงร้อง)';
  }

  interface RowRef { key: string; li: HTMLElement; sync: () => void }
  /** job id → its row. A row is drawn once and kept while its status stays the same, so a name being typed in it
   *  (keyboard, Thai input) survives listening, a song ending and reloads; only its buttons are updated (CORE). */
  const rows = new Map<string, RowRef>();

  function paint() {
    if (!info) { list.textContent = ''; rows.clear(); $('maForm').hidden = true; go.disabled = true; return; }
    const off = !info.keySet || !!info.keyProblem || info.capMinutes === 0;
    $('maForm').hidden = !info.keySet || !!info.keyProblem;
    $('maCapRow').hidden = !h.isAdmin();
    go.disabled = making || busy || off;
    paintGo();
    if (!mood.options.length) for (const m of info.moods) mood.append(new Option(m.name, m.id));
    paintCost();
    const want = info.jobs.map(j => ({ j, key: `${j.id}:${j.status}:${j.stale}` }));
    const next = new Map<string, RowRef>();
    for (const w of want) { const old = rows.get(w.j.id); next.set(w.j.id, old && old.key === w.key ? old : row(w.j, w.key)); }
    rows.clear();
    for (const [id, r] of next) rows.set(id, r);
    // in order, without moving a row that is already in its place
    want.forEach((w, at) => { const r = rows.get(w.j.id)!; if (list.children[at] !== r.li) list.insertBefore(r.li, list.children[at] ?? null); });
    while (list.children.length > want.length) list.lastElementChild!.remove();
    for (const r of rows.values()) r.sync();
  }

  function row(j: AiMusicJob, key: string): RowRef {
    const li = document.createElement('li'); li.className = 'mlrow';
    const body = document.createElement('div');
    const name = document.createElement('b');
    name.textContent = `${j.moodName} · ${j.lengthMs / 1000} วิ`;
    const sub = document.createElement('small');
    sub.textContent = `${thaiTime(j.createdAt)}${j.byName ? ` · ${j.byName}` : ''}${j.extra ? ` · "${j.extra}"` : ''}`;
    body.append(name, sub);
    li.append(body);
    const acts = document.createElement('div'); acts.className = 'addrow';
    if (j.status === 'draft') {
      const play = document.createElement('button'); play.className = 'btn';
      play.onclick = () => listen(j);
      const title = document.createElement('input');
      title.type = 'text'; title.maxLength = 80; title.autocomplete = 'off'; title.setAttribute('aria-label', 'ชื่อเพลง');
      title.value = `AI ${j.moodName} ${thaiTime(j.createdAt)}`.slice(0, 80);
      const cat = document.createElement('select'); cat.setAttribute('aria-label', 'หมวดเพลง');
      const keep = document.createElement('button'); keep.className = 'btn main'; keep.textContent = 'เก็บเข้าคลัง';
      keep.onclick = () => void keepIt(j, title.value, cat.value);
      const drop = document.createElement('button'); drop.className = 'btn danger';
      drop.onclick = () => {
        if (dropId !== j.id) { dropId = j.id; say('เพลงที่ทิ้งเอากลับมาไม่ได้ และนับเงินไปแล้ว กด "ยืนยันทิ้ง" ถ้าแน่ใจ'); paint(); return; }
        void dropIt(j);
      };
      acts.append(play, keep, drop);
      body.append(title, cat, acts);
      return {
        key, li, sync() {
          play.textContent = playing === j.id ? '■ หยุด' : '▶ ฟัง';
          keep.disabled = busy; drop.disabled = busy;
          drop.textContent = dropId === j.id ? 'ยืนยันทิ้ง' : 'ทิ้ง';
          // the categories arrive with the library list: filled once, then left to the admin's choice
          if (!cat.options.length) for (const c of h.categories()) cat.append(new Option(c.name, c.id, false, c.id === MOOD_CATEGORY[j.mood]));
        }
      };
    }
    if (j.status === 'making') {
      const note = document.createElement('small');
      note.className = j.stale ? 'err-text' : '';
      note.textContent = j.stale
        ? 'ไม่ได้รับคำตอบจาก ElevenLabs (การเชื่อมต่อขาด) · อาจถูกคิดเงินแล้ว จึงนับรวมในเพดาน'
        : 'กำลังแต่ง… (เปิดจากอีกเครื่องหรือยังไม่เสร็จ) กดโหลดใหม่ภายหลัง';
      const btn = document.createElement('button'); btn.className = 'btn';
      btn.textContent = j.stale ? 'ลบออกจากรายการ' : 'โหลดใหม่';
      btn.onclick = () => void (j.stale ? dropIt(j) : load());
      acts.append(btn);
      body.append(note, acts);
      return { key, li, sync() { btn.disabled = busy; } };
    }
    // failed this month
    const note = document.createElement('small'); note.className = 'err-text';
    note.textContent = `แต่งไม่สำเร็จ: ${j.error ?? 'ไม่ทราบสาเหตุ'}${j.charged ? '' : ' · ไม่นับเงิน'}`;
    body.append(note);
    return { key, li, sync() { /* nothing to update */ } };
  }

  async function load() {
    const off = h.online();
    if (off) { info = null; say(`ยังเปิดส่วน AI แต่งเพลงไม่ได้: ${off}`); paint(); return; }
    try {
      info = await api.aiMusic();
      if (info.keyProblem) say(info.keyProblem);
      else if (!info.keySet) say('ยังไม่ได้ตั้งคีย์ ElevenLabs จึงยังแต่งเพลงไม่ได้: ผู้ดูแลระบบตั้ง Worker secret ชื่อ ELEVENLABS_API_KEY (Cloudflare → Workers → craftstudio → Settings → Variables and Secrets) แล้ว deploy ใหม่ 1 ครั้ง');
      if (document.activeElement !== $in('maCap')) $in('maCap').value = String(info.capMinutes);
      paint();
    } catch (e) { say('โหลดส่วน AI แต่งเพลงไม่สำเร็จ: ' + errText(e)); }
  }

  function open() {
    if (!making) { say(''); dropId = null; len.value = String(fitSeconds()); }
    void load();
  }

  function stop() { audio.pause(); audio.removeAttribute('src'); audio.load(); playing = null; }
  function listen(j: AiMusicJob) {
    if (playing === j.id || !j.file) { stop(); paint(); return; }
    stop(); h.stopOthers();
    playing = j.id; audio.src = j.file;
    audio.play().catch(e => { playing = null; say('เล่นเพลงที่แต่งไม่ได้: ' + errText(e)); paint(); });
    paint();
  }
  audio.onended = () => { playing = null; paint(); };
  audio.onerror = () => { if (playing) { say('เปิดไฟล์เพลงที่แต่งไม่ได้ (อาจถูกทิ้งจากอีกเครื่อง) รายการโหลดใหม่ให้แล้ว'); playing = null; void load(); } };
  new MutationObserver(() => { if ($('shVMusicLib').hidden && playing) stop(); })
    .observe($('shVMusicLib'), { attributes: true, attributeFilter: ['hidden'] });

  len.oninput = paintCost;
  $('maFit').onclick = () => { len.value = String(fitSeconds()); paintCost(); };

  go.onclick = async () => {
    if (making || !info) return;
    const ms = lengthMs();
    if (!Number.isInteger(ms) || ms < 3000 || ms > 180_000) { say('ความยาวเพลงต้องอยู่ระหว่าง 3 ถึง 180 วินาที'); return; }
    making = true; tick = 0; say('');
    const timer = setInterval(() => { tick++; paintGo(); }, 1000);   // the button only: the list keeps what is being typed
    paint();
    try {
      const r = await api.aiMusicMake({ mood: mood.value, extra: $in('maExtra').value, lengthMs: ms });
      say(`แต่งเสร็จแล้ว (${r.job.lengthMs / 1000} วินาที) กด "▶ ฟัง" ก่อน ชอบแล้วกด "เก็บเข้าคลัง"`, true);
      toast('AI แต่งเพลงเสร็จแล้ว ฟังได้ในคลังเพลง');
    } catch (e) {
      const status = (e as { status?: number }).status;
      say(status === 0
        ? 'การเชื่อมต่อขาดระหว่างรอ ElevenLabs · ถ้ากลับมาเชื่อมต่อได้ภายในราว 30 วินาที เพลงจะยังเก็บไว้ให้ ดูในรายการด้านล่าง · ถ้านานกว่านั้น เพลงอาจหายทั้งที่ถูกคิดเงินแล้ว (รายการจะขึ้นว่าไม่ได้รับคำตอบ)'
        : 'แต่งเพลงไม่สำเร็จ: ' + errText(e));
    } finally {
      clearInterval(timer);
      making = false;
      await load();
    }
  };

  async function keepIt(j: AiMusicJob, name: string, categoryId: string) {
    if (busy || !j.file) return;
    if (!name.trim()) { say('ตั้งชื่อเพลงก่อนเก็บเข้าคลัง'); return; }
    if (!categoryId) { say('เลือกหมวดเพลงก่อน'); return; }
    busy = true; paint(); say('กำลังอ่านความยาวเพลง…');
    let durationMs = j.lengthMs, note = '';
    try {
      const res = await fetch(j.file, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`โหลดไฟล์ไม่ได้ (${res.status})`);
      durationMs = Math.round(await (await videoConvert()).probeMusic(await res.blob(), 'ai.mp3') * 1000);
    } catch (e) {
      note = ` (อ่านความยาวจริงไม่ได้: ${errText(e)} จึงใช้ความยาวที่ขอไว้ ${j.lengthMs / 1000} วินาที)`;
    }
    try {
      const r = await api.aiMusicKeep(j.id, { name, categoryId, durationMs });
      stop();
      say(`เก็บ "${r.music.name}" เข้าคลังแล้ว ทุกคนในกลุ่มเลือกใช้ได้${note}`, true);
      toast(`เก็บเพลง "${r.music.name}" เข้าคลังแล้ว`);
      await h.reload();
    } catch (e) { say('เก็บเข้าคลังไม่สำเร็จ: ' + errText(e)); }
    finally { busy = false; await load(); }
  }

  async function dropIt(j: AiMusicJob) {
    busy = true; paint();
    try {
      await api.aiMusicDrop(j.id);
      if (playing === j.id) stop();
      say(j.status === 'draft' ? 'ทิ้งเพลงนี้แล้ว (ยังนับในยอดเดือนนี้)' : 'ลบออกจากรายการแล้ว (ยังนับในยอดเดือนนี้)', true);
    } catch (e) { say('ทำไม่สำเร็จ: ' + errText(e)); }
    finally { dropId = null; busy = false; await load(); }
  }

  $('maCapSave').onclick = async () => {
    const n = Number($in('maCap').value);
    if (!Number.isInteger(n) || n < 0 || n > 600) { say('เพดานต่อเดือนต้องเป็นจำนวนเต็ม 0 ถึง 600 นาที (0 = ปิด)'); return; }
    try { await api.aiMusicCap(n); say(n ? `ตั้งเพดานเป็น ${n} นาทีต่อเดือนแล้ว` : 'ปิดการให้ AI แต่งเพลงแล้ว (เพดาน 0)', true); }
    catch (e) { say('บันทึกเพดานไม่สำเร็จ: ' + errText(e)); }
    await load();
  };

  /** repaint: the library's categories arrived (the drafts' category lists) */
  return { open, repaint: paint, stopPlaying() { if (playing) { stop(); paint(); } } };
}
